import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { api } from '@/lib/api';
import { env } from '@/lib/env';
import { useAuthStore } from '@/store/auth-store';
import type { NotificationType } from '@/types/profile';
import type { User } from '@/types/user';
import { staticConfig } from '@/lib/swr-config';

interface UpdateUserSettingsArg {
  firstName?: string;
  lastName?: string;
  notificationPrefs: NotificationType[];
  smsQuietHoursEnabled?: boolean;
  smsQuietHoursStartTime?: string;
  smsQuietHoursEndTime?: string;
}

interface AcceptCaregiverTermsArg {
  caregiverTermsAcceptedAt: string;
  caregiverTermsVersion: string;
}

export function useMyUser() {
  const updateAuthUser = useAuthStore(state => state.updateUser);

  const { data, error, isLoading, isValidating, mutate } = useSWR<User>(
    '/api/v1/users/me',
    url => api.get(url),
    {
      ...staticConfig,
      revalidateOnFocus: false,
      onSuccess: updatedUser => {
        updateAuthUser(updatedUser);
      },
    }
  );

  const { trigger: updateNotificationPrefs, isMutating: isUpdatingNotificationPrefs } =
    useSWRMutation(
      '/api/v1/users/me',
      async (url: string, { arg }: { arg: UpdateUserSettingsArg }) => {
        const updated = await api.patch<User>(url, arg);
        return updated;
      },
      {
        onSuccess: updated => {
          mutate(updated, false);
          updateAuthUser(updated);
        },
      }
    );

  const { trigger: acceptCaregiverTerms, isMutating: isAcceptingCaregiverTerms } = useSWRMutation(
    '/api/v1/users/me',
    async (url: string) => {
      const acceptedAt = new Date().toISOString();
      const update: AcceptCaregiverTermsArg = {
        caregiverTermsAcceptedAt: acceptedAt,
        caregiverTermsVersion: env.clientBookingTermsVersion,
      };
      const updated = await api.patch<User>(url, update);
      return updated;
    },
    {
      onSuccess: updated => {
        mutate(updated, false);
        updateAuthUser(updated);
      },
    }
  );

  return {
    user: data,
    error,
    isLoading,
    isValidating,
    isUpdatingNotificationPrefs,
    isAcceptingCaregiverTerms,
    updateNotificationPrefs,
    acceptCaregiverTerms,
    mutate,
  };
}
